import { colors, typography } from '@dixa/lib-shared-tokens';
import { BitmapText } from 'pixi.js';
import type { Graphics as PixiGraphicsObject } from 'pixi.js';
import { useCallback } from 'react';

import { textStyles } from 'fable/flowEditor/components/flowManager/canvas/node/textStyles';
import { hexStringToNumber } from 'fable/flowEditor/components/flowManager/canvas/renderers/common/colors';
import { Offset } from 'fable/flowEditor/components/flowManager/canvas/utils/text/baseTextParser/baseTextParser';

type UsePixiBadgeOptions = {
  text: string;
  variant: 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'error';
  offset: Offset;
};

type UsePixiBadgeResult = (graphics: PixiGraphicsObject) => void;

export const usePixiBadge = ({
  text,
  variant = 'primary',
  offset,
}: UsePixiBadgeOptions): UsePixiBadgeResult =>
  useCallback(
    (graphics) => {
      const padding = 8;
      const height = 24;

      const badgeText = new BitmapText(text, {
        ...textStyles.semibold,
        fontSize: 12,
        tint: hexStringToNumber(badgeVariants[variant].text),
      });
      badgeText.anchor.set(0, 0.5);
      badgeText.position.set(offset.x + padding, offset.y + 10);

      graphics.beginFill(hexStringToNumber(badgeVariants[variant].bg));
      graphics.drawRoundedRect(
        offset.x,
        offset.y,
        badgeText.textWidth + padding * 2,
        height,
        height / 2,
      );
      graphics.endFill();

      graphics.addChild(badgeText);
    },
    [offset.x, offset.y, text, variant],
  );

const badgeVariants: Record<
  UsePixiBadgeOptions['variant'],
  {
    bg: string;
    text: string;
  }
> = {
  primary: {
    bg: colors.purple['100'],
    text: colors.purple['700'],
  },
  secondary: {
    bg: colors.neutral['100'],
    text: typography.color.secondary,
  },
  success: {
    bg: colors.green['100'],
    text: colors.green['700'],
  },
  info: {
    bg: colors.blue['100'],
    text: colors.blue['700'],
  },
  warning: {
    bg: colors.orange['100'],
    text: colors.orange['800'],
  },
  error: {
    bg: colors.red['100'],
    text: colors.red['700'],
  },
};
